import React, { useState, useEffect, useCallback } from "react";
import { useDataProvider, useNotify } from "react-admin";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { apiGet, apiPost, apiDelete } from "../../utils/apiClient";

export default function LocationDiscountPlanTab({ locationId }) {
  const [assignment, setAssignment] = useState(undefined);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const fetchAssignment = useCallback(async () => {
    try {
      const data = await apiGet(`discount-plans/locations/${locationId}`);
      setAssignment(data);
      setSelectedPlanId(data?.discountPlanId ?? "");
    } catch {
      setAssignment(null);
      setSelectedPlanId("");
    }
  }, [locationId]);

  useEffect(() => {
    fetchAssignment();
    dataProvider
      .getList("discount-plans", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then(({ data }) => setPlans(data));
  }, [locationId, fetchAssignment, dataProvider]);

  const handleAssign = async () => {
    if (!selectedPlanId) return;
    setSaving(true);
    try {
      await apiPost(`discount-plans/locations/${locationId}`, {
        discountPlanId: selectedPlanId,
      });
      await fetchAssignment();
      notify("Discount plan assigned", { type: "success" });
    } catch {
      notify("Error assigning plan", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await apiDelete(`discount-plans/locations/${locationId}`);
      setAssignment(null);
      setSelectedPlanId("");
      notify("Removed — location will use the default plan", {
        type: "success",
      });
    } catch {
      notify("Error removing plan assignment", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (assignment === undefined) return <CircularProgress sx={{ m: 2 }} />;

  const assignedPlanId = assignment?.discountPlanId ?? null;
  const assignedPlanName =
    assignment?.discountPlan?.name ||
    plans.find((p) => p.id === assignedPlanId)?.name ||
    null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Current assignment
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {assignedPlanName ?? "Using default plan"}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Discount Plan</InputLabel>
          <Select
            value={selectedPlanId}
            label="Discount Plan"
            onChange={(e) => setSelectedPlanId(e.target.value)}
          >
            {plans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
                {p.isDefault ? " (default)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedPlanId || saving}
        >
          Assign
        </Button>

        {assignedPlanId && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemove}
            disabled={saving}
          >
            Remove (revert to default)
          </Button>
        )}
      </Stack>
    </Box>
  );
}
